import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, MapPin, Calendar, User, CircleNotch, X, ArrowUpRight, CaretLeft, CaretRight } from '@phosphor-icons/react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { ContactSection } from '../components/ContactSection';
import { PageMeta } from '../components/PageMeta';
import { Breadcrumb } from '../components/Breadcrumb';
import { QuickActions } from '../components/QuickActions';
import { SchemaScript } from '../components/SchemaMarkup';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProjectDetailPage() {
  const { slug } = useParams();
  const [project, setProject] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightboxIndex, setLightboxIndex] = useState(null);

  useEffect(() => {
    setLoading(true);
    axios
      .get(`${API}/portfolio/${slug}`)
      .then(async (r) => {
        setProject(r.data);
        // Fetch same-category peers first; if there are fewer than 3, fall
        // back to any other published projects so the "More Work" strip is
        // never empty on categories with a single project (hospitality/commercial).
        const sameCat = (await axios.get(`${API}/portfolio`, { params: { category: r.data.category } })).data || [];
        const remaining = 3 - sameCat.filter((p) => p.slug !== slug).length;
        let others = [];
        if (remaining > 0) {
          others = ((await axios.get(`${API}/portfolio`)).data || [])
            .filter((p) => p.category !== r.data.category);
        }
        const combined = [...sameCat, ...others].filter((p) => p.slug !== slug);
        // Dedupe by slug (preserve first-seen order)
        const seen = new Set();
        const unique = combined.filter((p) => (seen.has(p.slug) ? false : seen.add(p.slug)));
        setRelated(unique.slice(0, 3));
      })
      .catch(() => setProject(null))
      .finally(() => setLoading(false));
  }, [slug]);

  useEffect(() => {
    if (lightboxIndex === null) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setLightboxIndex(null);
      if (e.key === 'ArrowRight') setLightboxIndex((i) => (project ? (i + 1) % project.images.length : 0));
      if (e.key === 'ArrowLeft') setLightboxIndex((i) => (project ? (i - 1 + project.images.length) % project.images.length : 0));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightboxIndex, project]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gallery flex items-center justify-center">
        <CircleNotch weight="bold" className="w-12 h-12 animate-spin text-bronze" />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen bg-gallery">
        <Navbar />
        <div className="max-w-4xl mx-auto px-6 py-24 text-center">
          <h1 className="font-serif text-4xl mb-4">Project not found</h1>
          <Link to="/projects" className="text-bronze font-bold uppercase tracking-widest text-xs">
            ← Back to All Projects
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const projectSchema = {
    '@context': 'https://schema.org',
    '@type': 'CreativeWork',
    name: project.title,
    description: project.description,
    image: project.hero,
    creator: { '@type': 'Organization', name: 'IHG Industry', url: 'https://ihgind.com' },
    ...(project.location ? { locationCreated: { '@type': 'Place', name: project.location } } : {}),
    ...(project.year ? { dateCreated: project.year } : {}),
  };

  return (
    <div className="min-h-screen bg-gallery" data-testid="project-detail-page">
      <PageMeta
        title={project.seo_title || `${project.title} | IHG Industry`}
        description={project.seo_description || project.description || `${project.title} — architectural metalwork by IHG Industry.`}
        ogImage={project.hero}
      />
      <SchemaScript schema={projectSchema} />
      <Navbar />
      <QuickActions variant="sidebar" />
      <QuickActions variant="fab" />

      {/* Hero */}
      <section className="relative h-[70vh] md:h-[80vh] bg-obsidian overflow-hidden">
        {project.hero && (
          <img
            src={project.hero}
            alt={project.title}
            className="absolute inset-0 w-full h-full object-cover opacity-70"
            loading="eager"
            fetchPriority="high"
            decoding="sync"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/40 to-transparent" />
        <div className="relative h-full flex items-end">
          <div className="w-full max-w-[1800px] mx-auto px-6 md:px-12 lg:px-24 pb-12 md:pb-16">
            <div className="text-white/70 text-xs font-bold uppercase tracking-[0.3em] mb-4">
              {project.category_label}
            </div>
            <h1 className="font-serif text-4xl md:text-6xl lg:text-7xl text-white leading-tight max-w-4xl">
              {project.title}
            </h1>
            <div className="mt-6 flex flex-wrap gap-6 text-white/80 text-sm">
              {project.location && (
                <div className="flex items-center gap-2"><MapPin weight="fill" className="w-4 h-4 text-bronze" /> {project.location}</div>
              )}
              {project.client && (
                <div className="flex items-center gap-2"><User weight="fill" className="w-4 h-4 text-bronze" /> {project.client}</div>
              )}
              {project.year && (
                <div className="flex items-center gap-2"><Calendar weight="fill" className="w-4 h-4 text-bronze" /> {project.year}</div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Breadcrumb */}
      <div className="bg-white border-b border-black/5">
        <div className="max-w-[1800px] mx-auto px-6 md:px-12 lg:px-24 py-4">
          <Breadcrumb
            items={[{ label: 'Projects', href: '/projects' }]}
            currentPage={project.title}
          />
        </div>
      </div>

      {/* Content */}
      <section className="max-w-[1800px] mx-auto px-6 md:px-12 lg:px-24 py-16 md:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2 space-y-8">
            {project.description && (
              <div>
                <div className="text-xs font-bold uppercase tracking-[0.3em] text-bronze mb-3">Overview</div>
                <p className="font-serif text-2xl md:text-3xl leading-snug text-obsidian">
                  {project.description}
                </p>
              </div>
            )}
            {project.scope && (
              <div>
                <div className="text-xs font-bold uppercase tracking-[0.3em] text-concrete mb-3">Scope of Work</div>
                <p className="text-base md:text-lg text-obsidian/80 leading-relaxed">{project.scope}</p>
              </div>
            )}
          </div>

          <aside className="space-y-8 lg:pl-8 lg:border-l lg:border-black/10">
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.3em] text-concrete mb-3">At a Glance</div>
              <dl className="divide-y divide-black/5 text-sm">
                {project.category_label && (
                  <div className="flex justify-between py-2">
                    <dt className="text-concrete">Category</dt>
                    <dd className="text-obsidian font-medium">{project.category_label}</dd>
                  </div>
                )}
                {project.location && (
                  <div className="flex justify-between py-2">
                    <dt className="text-concrete">Location</dt>
                    <dd className="text-obsidian font-medium text-right">{project.location}</dd>
                  </div>
                )}
                {project.client && (
                  <div className="flex justify-between py-2">
                    <dt className="text-concrete">Client</dt>
                    <dd className="text-obsidian font-medium text-right">{project.client}</dd>
                  </div>
                )}
                {project.year && (
                  <div className="flex justify-between py-2">
                    <dt className="text-concrete">Year</dt>
                    <dd className="text-obsidian font-medium">{project.year}</dd>
                  </div>
                )}
                {project.images?.length > 0 && (
                  <div className="flex justify-between py-2">
                    <dt className="text-concrete">Images</dt>
                    <dd className="text-obsidian font-medium">{project.images.length} views</dd>
                  </div>
                )}
              </dl>
            </div>
            {(project.materials || []).length > 0 && (
              <div>
                <div className="text-xs font-bold uppercase tracking-[0.3em] text-concrete mb-3">Materials</div>
                <div className="flex flex-wrap gap-2">
                  {project.materials.map((m) => (
                    <span key={m} className="px-3 py-1 text-xs bg-white border border-black/10 uppercase tracking-wider">{m}</span>
                  ))}
                </div>
              </div>
            )}
            {(project.finishes || []).length > 0 && (
              <div>
                <div className="text-xs font-bold uppercase tracking-[0.3em] text-concrete mb-3">Finishes</div>
                <div className="flex flex-wrap gap-2">
                  {project.finishes.map((f) => (
                    <span key={f} className="px-3 py-1 text-xs bg-white border border-bronze/40 text-bronze uppercase tracking-wider">{f}</span>
                  ))}
                </div>
              </div>
            )}
            {(project.related_capabilities || []).length > 0 && (
              <div>
                <div className="text-xs font-bold uppercase tracking-[0.3em] text-concrete mb-3">Capabilities Used</div>
                <div className="space-y-2">
                  {project.related_capabilities.map((c) => (
                    <Link
                      key={c}
                      to={`/capabilities/${c}`}
                      className="flex items-center gap-2 text-sm text-obsidian hover:text-bronze transition-colors"
                    >
                      <ArrowUpRight weight="bold" className="w-3.5 h-3.5" />
                      {c.replace(/-/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </aside>
        </div>
      </section>

      {/* Feature image break — the second gallery image, full-width, no crop */}
      {project.images?.length > 1 && (
        <section className="relative w-full bg-obsidian" data-testid="project-feature-break">
          <img
            src={project.images[1]}
            alt={`${project.title} — detail`}
            loading="lazy"
            decoding="async"
            className="w-full h-[45vh] md:h-[70vh] object-cover"
          />
        </section>
      )}

      {/* Case study narrative — Challenge / Approach / Outcome */}
      <section className="bg-white py-16 md:py-24" data-testid="project-case-study">
        <div className="max-w-[1800px] mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-xs font-bold uppercase tracking-[0.3em] text-bronze mb-3">Case Study</div>
          <h2 className="font-serif text-3xl md:text-5xl leading-tight text-obsidian mb-12 max-w-4xl">
            How IHG Industry delivered <span className="text-bronze">{project.title}</span>.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.4em] text-concrete mb-4">01 · The Brief</div>
              <p className="text-base md:text-lg text-obsidian/80 leading-relaxed">
                {project.description || `A ${project.category_label?.toLowerCase() || 'commercial'} project${project.location ? ` in ${project.location}` : ''} requiring precision metalwork, sourced materials and a coordinated install schedule.`}
              </p>
            </div>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.4em] text-concrete mb-4">02 · Our Approach</div>
              <p className="text-base md:text-lg text-obsidian/80 leading-relaxed">
                {project.scope || 'From material selection through in-house fabrication, finishing and on-site installation — every element was engineered, sampled and signed off by the IHG Industry craft team before it left the workshop.'}
              </p>
              {project.materials?.length > 0 && (
                <div className="mt-4 text-sm text-concrete">
                  <span className="font-bold text-obsidian">Materials: </span>{project.materials.join(', ')}
                </div>
              )}
            </div>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.4em] text-concrete mb-4">03 · The Outcome</div>
              <p className="text-base md:text-lg text-obsidian/80 leading-relaxed">
                A completed{project.year ? ` ${project.year}` : ''} install that delivered the design intent on programme — {project.images?.length || 0} documented views showcase the finished metalwork in context.
              </p>
              <Link to="/collaborate" className="inline-flex items-center gap-2 mt-4 text-xs font-bold uppercase tracking-widest text-obsidian hover:text-bronze">
                Plan a similar project <ArrowUpRight weight="bold" className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery — mixed bento layout, first image is featured 2×2 */}
      {project.images.length > 1 && (
        <section className="bg-gallery py-16 md:py-24" data-testid="project-gallery">
          <div className="max-w-[1800px] mx-auto px-6 md:px-12 lg:px-24">
            <div className="flex items-baseline justify-between mb-6">
              <div className="text-xs font-bold uppercase tracking-[0.3em] text-bronze">Gallery · {project.images.length} views</div>
              <div className="text-xs text-concrete uppercase tracking-widest">Click any tile to expand</div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 md:auto-rows-[220px] lg:auto-rows-[260px] gap-3 md:gap-4">
              {project.images.map((img, i) => {
                // Only the very first image is the 2×2 hero tile; the rest are
                // uniform 1×1 tiles so row heights line up cleanly.
                const isHero = i === 0;
                return (
                  <button
                    key={img}
                    type="button"
                    onClick={() => setLightboxIndex(i)}
                    className={`group relative overflow-hidden bg-neutral-100 ${
                      isHero ? 'md:col-span-2 md:row-span-2 col-span-2 aspect-square md:aspect-auto' : 'aspect-[4/3] md:aspect-auto'
                    }`}
                    data-testid={`gallery-thumb-${i}`}
                  >
                    <img
                      src={img}
                      alt={`${project.title} — image ${i + 1}`}
                      loading="lazy"
                      decoding="async"
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-obsidian/0 group-hover:bg-obsidian/20 transition-colors" />
                    <div className="absolute bottom-3 left-3 opacity-0 group-hover:opacity-100 transition-opacity text-white text-[10px] font-mono tracking-widest">
                      {String(i + 1).padStart(2, '0')} / {String(project.images.length).padStart(2, '0')}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Lightbox */}
      {lightboxIndex !== null && (
        <div
          className="fixed inset-0 z-[200] bg-obsidian/95 flex items-center justify-center p-4"
          onClick={() => setLightboxIndex(null)}
          role="dialog"
          aria-label="Project image viewer"
          data-testid="project-lightbox"
        >
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setLightboxIndex(null); }}
            className="absolute top-6 right-6 text-white/80 hover:text-white z-10"
            aria-label="Close"
            data-testid="lightbox-close"
          >
            <X weight="bold" className="w-8 h-8" />
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setLightboxIndex((i) => (i - 1 + project.images.length) % project.images.length); }}
            className="absolute left-6 top-1/2 -translate-y-1/2 text-white/80 hover:text-white"
            aria-label="Previous"
            data-testid="lightbox-prev"
          >
            <CaretLeft weight="bold" className="w-10 h-10" />
          </button>
          <img
            src={project.images[lightboxIndex]}
            alt={`${project.title} — expanded view`}
            className="max-h-[85vh] max-w-[90vw] object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setLightboxIndex((i) => (i + 1) % project.images.length); }}
            className="absolute right-6 top-1/2 -translate-y-1/2 text-white/80 hover:text-white"
            aria-label="Next"
            data-testid="lightbox-next"
          >
            <CaretRight weight="bold" className="w-10 h-10" />
          </button>
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/70 text-xs font-mono">
            {lightboxIndex + 1} / {project.images.length}
          </div>
        </div>
      )}

      {/* Related */}
      {related.length > 0 && (
        <section className="bg-gallery py-16 md:py-24">
          <div className="max-w-[1800px] mx-auto px-6 md:px-12 lg:px-24">
            <div className="flex items-baseline justify-between mb-8">
              <h2 className="font-serif text-3xl md:text-4xl">More <span className="text-bronze">{project.category_label}</span> Work</h2>
              <Link to="/projects" className="text-xs font-bold uppercase tracking-widest text-obsidian hover:text-bronze inline-flex items-center gap-2">
                All Projects <ArrowUpRight weight="bold" className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
              {related.map((p) => (
                <Link key={p.slug} to={`/projects/${p.slug}`} className="group block bg-white overflow-hidden" data-testid={`related-${p.slug}`}>
                  <div className="relative aspect-[4/3] overflow-hidden bg-neutral-100">
                    <img src={p.hero} alt={p.title} loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                  <div className="p-6">
                    <h3 className="font-serif text-xl group-hover:text-bronze transition-colors">{p.title}</h3>
                    {p.location && <div className="text-xs text-concrete mt-1">{p.location}</div>}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <div className="max-w-[1800px] mx-auto px-6 md:px-12 lg:px-24 py-10">
        <Link to="/projects" className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-obsidian hover:text-bronze" data-testid="back-to-projects">
          <ArrowLeft weight="bold" className="w-4 h-4" /> All Projects
        </Link>
      </div>

      <ContactSection />
      <Footer />
    </div>
  );
}
